import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n/I18nContext";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight mb-4 text-ink-text">
      {title}
    </h2>
    <div className="space-y-4 text-sm sm:text-base text-muted leading-relaxed">
      {children}
    </div>
  </section>
);

export default function Privacy({ forceLang }) {
  const { lang, setLang, isEn } = useI18n();

  useEffect(() => {
    if (forceLang && lang !== forceLang) {
      setLang(forceLang);
    }
  }, [forceLang, lang, setLang]);

  const activeEn = isEn || forceLang === "en";

  return (
    <main className="min-h-screen bg-page text-ink-text">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16 sm:py-20">
        <div className="mb-10">
          <Link
            to={activeEn ? "/en" : "/"}
            className="mono text-[11px] uppercase tracking-[0.22em] text-brand-cyan hover:text-brand-cyan/80 transition-colors"
          >
            ← {activeEn ? "Back to Home" : "На главную"}
          </Link>
        </div>

        <div className="glass rounded-3xl p-8 sm:p-10 lg:p-12">
          {activeEn ? (
            <div>
              <div className="mb-10">
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-brand-cyan mb-4">
                  Privacy Policy
                </div>
                <h1 className="text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-tight mb-4">
                  Privacy & Data Protection Policy
                </h1>
                <p className="text-muted leading-relaxed max-w-3xl">
                  This Privacy Policy outlines how InBoost AI Engine collects, processes, and protects information submitted through inboost.pro and related services.
                </p>
              </div>

              <Section title="1. Overview">
                <p>
                  InBoost respects your privacy and is committed to protecting business contact information. We do not sell, rent, or trade user data with external commercial third parties.
                </p>
                <p>
                  For any privacy inquiries or data requests, please contact our privacy compliance team at{" "}
                  <a
                    href="mailto:inbound@inboost.pro"
                    className="text-brand-cyan hover:text-brand-cyan/80 transition-colors"
                  >
                    inbound@inboost.pro
                  </a>.
                </p>
              </Section>

              <Section title="2. Information We Collect">
                <p>
                  We only process information that you voluntarily provide when requesting a technical demo or submitting an infrastructure assessment:
                </p>
                <ul className="list-disc pl-5 space-y-1.5 text-muted">
                  <li>Full name, corporate email address, company name, and phone number (if provided).</li>
                  <li>Technical workload characteristics (GPU fleet size, current scheduler, utilization estimates).</li>
                  <li>Technical log data (standard server access logs and anonymous aggregate analytics).</li>
                </ul>
              </Section>

              <Section title="3. Purpose of Processing">
                <p>
                  Submitted data is utilized exclusively to:
                </p>
                <ul className="list-disc pl-5 space-y-1.5 text-muted">
                  <li>Deliver customized ROI and infrastructure assessment reports.</li>
                  <li>Coordinate scheduled technical briefings and architecture demos.</li>
                  <li>Maintain secure, reliable platform operations.</li>
                </ul>
              </Section>

              <Section title="4. Data Security & Retention">
                <p>
                  We apply modern cryptographic and enterprise-grade organizational safeguards to protect submitted information against unauthorized access, loss, or alteration. Data is retained only for as long as necessary to fulfill the requested business communication.
                </p>
              </Section>

              <Section title="5. Contact">
                <p>
                  For questions regarding data processing, deletion requests, or data protection practices, reach out via{" "}
                  <a href="mailto:inbound@inboost.pro" className="text-brand-cyan hover:underline">
                    inbound@inboost.pro
                  </a>.
                </p>
              </Section>
            </div>
          ) : (
            <div>
              <div className="mb-10">
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-brand-cyan mb-4">
                  Privacy
                </div>
                <h1 className="text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-tight mb-4">
                  Политика обработки персональных данных
                </h1>
                <p className="text-muted leading-relaxed max-w-3xl">
                  Настоящая Политика определяет порядок обработки персональных данных и меры по обеспечению
                  безопасности персональных данных, предпринимаемые ООО «ИнБуст» при осуществлении деятельности
                  и использовании сайта inboost.pro.
                </p>
              </div>

              <Section title="1. Общие положения">
                <p>
                  Оператором персональных данных является ООО «ИнБуст», ИНН 7730334680, ОГРН 1257700054336,
                  КПП 771001001, адрес: 127006, г. Москва, вн.тер.г. Муниципальный округ Тверской,
                  ул. Садовая-Триумфальная, д.16, стр.3, помещение 2А/1.
                </p>
                <p>
                  Адрес электронной почты для обращений по вопросам обработки персональных данных:{" "}
                  <a
                    href="mailto:inbound@inboost.pro"
                    className="text-brand-cyan hover:text-brand-cyan/80 transition-colors"
                  >
                    inbound@inboost.pro
                  </a>
                  .
                </p>
                <p>
                  Оператор осуществляет обработку персональных данных в соответствии с законодательством
                  Российской Федерации, в том числе Федеральным законом от 27.07.2006 № 152-ФЗ «О персональных
                  данных».
                </p>
              </Section>

              <Section title="2. Цели обработки персональных данных">
                <p>
                  Персональные данные пользователей могут обрабатываться в целях:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-muted">
                  <li>предоставления информации о платформе InBoost;</li>
                  <li>организации демонстраций и технических консультаций;</li>
                  <li>подготовки расчетов ROI и оценки инфраструктуры;</li>
                  <li>взаимодействия с потенциальными клиентами и партнерами.</li>
                </ul>
              </Section>

              <Section title="3. Контакты по вопросам персональных данных">
                <p>
                  По любым вопросам, связанным с обработкой ваших персональных данных, вы можете обратиться
                  по адресу:{" "}
                  <a href="mailto:inbound@inboost.pro" className="text-brand-cyan hover:underline">
                    inbound@inboost.pro
                  </a>.
                </p>
              </Section>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}